import React, { useEffect, useState } from "react";
import IDepositForm, {
  EState,
  TDepositState,
  IUseDepositState,
} from "./IDepositForm";
import StepsHeader from "@market/components/StepsHeader/StepsHeader";
import StepButtons from "@appComponents/StepButtons/StepButtons";
import InfoBox from "@appComponents/InfoBox/InfoBox";
import FundsService from "@services/Funds.service";
import DepositQrCode from "../DepositQrCode/DepositQrCode";
import DepositFiatAccount from "../DepositFiatAccount/DepositFiatAccount";
import DepositFiatSelect, {
  fiatSelectOptions,
} from "@funds/components/DepositFiatSelect/DepositFiatSelect";
import Notice from "@appComponents/Notice/Notice";
import {
  animateScrollTo,
  normalizeBankAccountNumber,
} from "@features/utils/functions";
import { IDepositWallet } from "../DepositQrCode/IDepositQrCode";
import { useDepositForm } from "./useDepositForm";
import { FundsAppContext, FundsDispatchContext } from "@features/context";
import { MAIN_CURRENCY } from "@bundles/consts";
import { TReducers } from "@features/reducers";
import { connect } from "react-redux";
import { IBankAccountProperties } from "../WithdrawAmount/IWithdrawAmount";
import { updateBankAccounts } from "@funds/features/action.funds";
import { WarningBox } from "../WarningBox/WarningBox";
import {
  fdtDepositRestrictedCountries,
  fdtConsiderStableCoins,
} from "../WarningBox/warningMessages";
import { getCountryNamesArray } from "@funds/features/helpers/functions";
import CustodyOfFundsNotice from "../CustodyOfFundsNotice/CustodyOfFundsNotice";
import Checkbox from "@appComponents/Checkbox/Checkbox";
import { Divider, Typography } from "antd";

import * as style from "./DepositFormStyle.module.scss"

const isFiatSelected = (selectedCoin: string = "") => {
  const fiatValues = fiatSelectOptions.reduce(
    (prev, curr) => [...prev, curr.value],
    []
  );
  return fiatValues.includes(selectedCoin);
};

const stepsHeader = ["Select Type", "Deposit"];

const depositFiatProceedText =
  "You can freely transfer any amount without the need for prior declaration. The system will automatically deposit the net received amount into your account, deducting any applicable conversion or transaction fees imposed by our partner bank.";

const DepositForm: React.FunctionComponent<IDepositForm> = (props) => {
  const isMainCurrency = props.currency === MAIN_CURRENCY;
  const firstStep = isMainCurrency
    ? EState.mainCurrencyDeposit
    : EState.cryptoQrDeposit;
  const [state, setState] = useState<TDepositState>({
    step: firstStep,
    stepLoading: false,
  });
  const { dispatch, values, errors } = useDepositForm(state.step);
  const { appLink, investorId } = React.useContext(FundsAppContext);
  const isOnFinalStep =
    !isMainCurrency || state.step !== EState.mainCurrencyDeposit;
  const hasActiveBankAccount = props.bankAccounts.find(
    (a) => a.status === "active"
  );
  const hasOnlyUnacceptedBankAccount =
    props.bankAccounts.length === 1 &&
    props.bankAccounts[0].status === "email_sent";
  const isAddingBankAccount =
    state.step === EState.depositFiatCurrency && !hasActiveBankAccount;
  const isCryptoSelected = !isFiatSelected(values.selectedCoin);
  const blockedCountries = getCountryNamesArray(props.fiatRestrictedCountries);
  const isFirstStep =
    (isMainCurrency && state.step === EState.mainCurrencyDeposit) ||
    (!isMainCurrency && state.step === EState.cryptoQrDeposit);

  const handleChange = (data: Partial<IUseDepositState["values"]>) => {
    dispatch({ type: "editMode", values: { ...values, ...data }, errors: {} });
  };

  const fiatDepositBlocked =
    props.fiatRestrictedCountries.includes(
      hasActiveBankAccount?.country || ""
    ) || props.fiatRestrictedCountries.includes(props.countryOfResidence);

  const [usdtCheckboxAgreements, setUsdtCheckboxAgreements] = React.useState({
    agreement1: true,
    agreement2: true,
    agreement3: true,
  });
  useEffect(() => {
    if (props.history.location.hash) {
      const idWithoutHash = props.history.location.hash.substring(1);
      setTimeout(() => {
        animateScrollTo(undefined, document.getElementById(idWithoutHash));
      }, 500);
    }
  }, []);

  const nextStep = () => {
    if (isAddingBankAccount) {
      if (values.bic_swift && values.account_number) {
        setState((state) => ({ ...state, stepLoading: true }));
        FundsService.addBankAccount(investorId!, {
          currency: "bfusd",
          bic_swift: values.bic_swift,
          account_number: normalizeBankAccountNumber(values.account_number),
        }).then((res) => {
          props.updateBankAccounts(res);
          dispatch({ type: "addedBankAccount", id: res.data.id });
          setState((state) => ({ ...state, stepLoading: false }));
        });
      }
      return;
    }

    if (isOnFinalStep) {
      props.history.push(appLink);
      return;
    }

    if (isFiatSelected(values.selectedCoin)) {
      setState((prev) => ({ ...prev, step: EState.depositFiatCurrency }));
      dispatch({
        type: "setErrors",
        errors: { initial: "initial error disables Next button" },
      });
    } else {
      setState((prev) => ({ ...prev, step: EState.cryptoQrDeposit }));
    }
    animateScrollTo(undefined, document.getElementById("depositform"), 20);
  };

  const prevStep = () => {
    if (isFirstStep) return;

    setState((prev) => ({ ...prev, step: firstStep }));
    animateScrollTo(undefined, document.getElementById("depositform"), 20);
  };

  const onRadioChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.persist();
    handleChange({ selectedCoin: e.target.value });
  };

  const updateDepositWallets = (wallet: IDepositWallet) => {
    handleChange({
      wallets: values.wallets ? [...values.wallets, wallet] : [wallet],
      buttonStepHidden: { ...values.buttonStepHidden, crypto: false },
    });
  };

  const onUsdtToFiatAgreement = (name: string) => {
    setUsdtCheckboxAgreements((prev: any) => {
      return {
        ...prev,
        [name]: !prev[name],
      };
    });
  };

  useEffect(() => {
    if (state.step === EState.depositFiatCurrency) {
      setUsdtCheckboxAgreements({
        agreement1: false,
        agreement2: false,
        agreement3: false,
      });
    }
  }, [state.step]);

  const renderSteps = () => {
    switch (state.step) {
      case EState.mainCurrencyDeposit:
        return (
          <>
            <DepositFiatSelect
              fiatDisabled={fiatDepositBlocked}
              onRadioChange={onRadioChange}
              network={values.selectedCoin || ""}
            />

            <WarningBox
              texts={[
                fdtDepositRestrictedCountries(blockedCountries),
                fdtConsiderStableCoins,
              ]}
            />

            <CustodyOfFundsNotice
              noDivider={true}
              style={{ marginTop: "10px" }}
            />
          </>
        );
      case EState.cryptoQrDeposit:
        return (
          <>
            <DepositQrCode
              currency={props.currency}
              selectedCoin={values.selectedCoin}
              updateState={updateDepositWallets}
              wallets={values.wallets}
            />
          </>
        );
      case EState.depositFiatCurrency:
        return (
          <>
            <DepositFiatAccount
              currency={props.currency}
              values={values}
              errors={errors}
              selectedCoin={values.selectedCoin || ""}
            />
            {hasActiveBankAccount && (
              <>
                <InfoBox>
                  <p style={{ textAlign: "justify" }}>
                    {depositFiatProceedText}
                  </p>
                </InfoBox>

                <Divider />
                <Typography.Title level={5}>
                  Disclaimer: Terms of Fiat Deposit Conversion and Documentation
                  Requirements
                </Typography.Title>

                <Typography.Text>
                  By depositing funds, you acknowledge and agree to the
                  following terms regarding the conversion and verification of
                  Fiat Deposits on this platform:
                </Typography.Text>

                <div className={style.usdtToFiatAgreement}>
                  <Checkbox
                    onClick={() => onUsdtToFiatAgreement("agreement1")}
                    checked={usdtCheckboxAgreements.agreement1}
                    id="agreement1"
                  >
                    <Typography.Text>
                      <label htmlFor="agreement1">
                        If your deposit isn't in USD, our partner bank will
                        convert it to USD. This conversion may incur fees. BF
                        does not charge a conversion fee.
                      </label>
                    </Typography.Text>
                  </Checkbox>

                  <Checkbox
                    onClick={() => onUsdtToFiatAgreement("agreement2")}
                    checked={usdtCheckboxAgreements.agreement2}
                    id="agreement2"
                  >
                    <Typography.Text>
                      <label htmlFor="agreement2">
                        BF will convert your USD deposit to USDT at the
                        prevailing market rate, which may be less than 1 USD to
                        1 USDT, and credit the USDT amount to your balance.
                      </label>
                    </Typography.Text>
                  </Checkbox>

                  <Checkbox
                    onClick={() => onUsdtToFiatAgreement("agreement3")}
                    checked={usdtCheckboxAgreements.agreement3}
                    id="agreement3"
                  >
                    <Typography.Text>
                      <label htmlFor="agreement3">
                        I understand that after my fiat deposit is received,
                        BF’s partner bank requires the following documents for
                        verification and processing:
                        <br />
                        <ul>
                          <li>
                            Relationship Proof verifying my connection with BF
                            Virtual Assets Ltd. You can{" "}
                            <a
                              target="_blank"
                              href="https://signnow.com/s/ctXmFq1P?name_formula=RelationshipProof%20-%20FDT%20-%20BF%20Virtual%20Assets%20%7CFull%20Name%201%7CDate%20and%20Time%201"
                            >
                              sign it here.
                            </a>
                          </li>
                          <li>Purpose of Deposit</li>
                          <li>
                            Source of Funds (with supporting documentation)
                          </li>
                          <li>
                            Latest Proof of Address (POA) and Proof of Identity
                            (POI) dated within 2 months of the deposit
                          </li>
                          <li>Deposit Slip from your bank</li>
                        </ul>
                        <br />I acknowledge that BF’s customer service team will
                        contact me to collect these documents, and I accept that
                        there may be processing delays as these documents are
                        transmitted to the partner bank.
                      </label>
                    </Typography.Text>
                  </Checkbox>
                </div>
              </>
            )}
          </>
        );
    }
  };

  return (
    <div id="depositform" className={style.depositForm}>
      {props.bankFormAddressDataAvailable && (
        <StepsHeader
          steps={stepsHeader}
          current={isOnFinalStep || !isMainCurrency ? 2 : 1}
        />
      )}

      <div className={state.stepLoading ? style.blocked : undefined}>
        <FundsDispatchContext.Provider value={dispatch}>
          {renderSteps()}
        </FundsDispatchContext.Provider>
      </div>

      <StepButtons
        nextStep={nextStep}
        prevStep={prevStep}
        isFirstStep={isFirstStep}
        nextStepEnable={
          ((!!values.selectedCoin || !isMainCurrency) &&
            state.step !== EState.depositFiatCurrency) ||
          (state.step === EState.depositFiatCurrency &&
            ((Object.keys(errors).length === 0 &&
              !state.stepLoading &&
              !hasOnlyUnacceptedBankAccount) ||
              !hasActiveBankAccount) &&
            !!values.account_number &&
            !!values.bic_swift) ||
          (isOnFinalStep &&
            !isAddingBankAccount &&
            usdtCheckboxAgreements.agreement1 &&
            usdtCheckboxAgreements.agreement2 &&
            usdtCheckboxAgreements.agreement3)
        }
        nextText={isOnFinalStep && !isAddingBankAccount ? "Finish" : "Next"}
        nextHidden={
          (isOnFinalStep &&
            values.buttonStepHidden[isCryptoSelected ? "crypto" : "usdSwift"] &&
            !props.bankFormAddressDataAvailable) ||
          (state.step === EState.depositFiatCurrency &&
            !hasActiveBankAccount &&
            !isAddingBankAccount)
        }
      />
    </div>
  );
};

const mapDispatchToProps = (dispatch: Function) => ({
  updateBankAccounts: (data: IBankAccountProperties) =>
    dispatch(updateBankAccounts(data)),
});

const mapStateToProps = (store: TReducers) => ({
  bankFormAddressDataAvailable: store.fundsReducer.bankFormAddressDataAvailable,
  bankAccounts: store.fundsReducer.bankAccounts,
  countryOfResidence: store.userInfo.country,
  fiatRestrictedCountries: store.appSettings.fiat_restricted_countries,
});

export default React.memo(
  connect(mapStateToProps, mapDispatchToProps)(DepositForm)
);
